import React, { useState, useEffect } from "react";
import { MdOutlineDateRange } from "react-icons/md";
import { TfiPencil } from "react-icons/tfi";
import { FiSearch } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import API from "../../Utils/api";
import Bannerimg from "../../Assets/banner.png";
import Banner from "../../components/UserSideComponents/common/Banner";

// Skeleton Card
const BlogCardSkeleton = () => (
  <div className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 animate-pulse">
    <div className="w-full h-52 bg-gray-200" />
    <div className="p-5 space-y-3">
      <div className="h-3 bg-gray-200 rounded w-1/3" />
      <div className="h-5 bg-gray-200 rounded w-full" />
      <div className="h-5 bg-gray-200 rounded w-4/5" />
      <div className="h-3 bg-gray-200 rounded w-full" />
      <div className="h-3 bg-gray-200 rounded w-3/4" />
      <div className="h-8 bg-gray-200 rounded-lg w-28 mt-2" />
    </div>
  </div>
);

const ITEMS_PER_PAGE = 8;

const BlogPage = () => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await API.get("/blogs/all-blogs");
      const data = Array.isArray(res.data) ? res.data : res.data.data || [];
      setBlogs(data);
    } catch (err) {
      console.error("Error fetching blogs:", err);
      setError("Failed to load blogs.");
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const stripHtml = (html) => html?.replace(/<[^>]*>/g, "") || "";

  // Filter
  const filtered = blogs.filter(
    (b) =>
      b.title.toLowerCase().includes(search.toLowerCase()) ||
      stripHtml(b.content).toLowerCase().includes(search.toLowerCase()),
  );

  // Pagination
  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const paginated = filtered.slice(
    (currentPage - 1) * ITEMS_PER_PAGE,
    currentPage * ITEMS_PER_PAGE,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  // Featured blog = first blog
  const featuredBlog = !loading && blogs.length > 0 ? blogs[0] : null;
  const restBlogs = paginated.filter((b) => b._id !== featuredBlog?._id);

  return (
    <>
      <Banner heading="Our Blog" image={Bannerimg} />

      <div className="w-full px-4 sm:px-6 md:px-10 py-10 bg-[#F7F7F6] min-h-screen">
        {/* Header */}
        <div className="flex flex-col items-center justify-center text-center mb-10">
          <p className="text-[#92AD36] text-sm font-semibold tracking-widest uppercase">
            Our Company Blog
          </p>
          <h2 className="text-2xl md:text-4xl font-serif  text-black mt-3">
            Latest News & Updates
          </h2>
          <div className="flex mt-4 gap-2">
            <span className="w-10 h-1 bg-[#92AD36]" />
            <span className="w-3 h-1 bg-[#92AD36]" />
            <span className="w-5 h-1 bg-[#92AD36]" />
          </div>
        </div>

        {/* Search Bar */}
        <div className="flex justify-center mb-10">
          <div className="relative w-full max-w-lg">
            <FiSearch
              className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400"
              size={18}
            />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search blogs by title or content..."
              className="w-full pl-11 pr-4 py-3 rounded-xl border border-gray-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-[#92AD36] shadow-sm"
            />
            {search && (
              <button
                onClick={() => setSearch("")}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-red-400 transition text-xs"
              >
                ✕
              </button>
            )}
          </div>
        </div>

        {/* Error */}
        {error && !loading && (
          <div className="flex flex-col items-center py-20 text-center">
            <span className="text-5xl mb-4">😕</span>
            <p className="text-gray-500">{error}</p>
            <button
              onClick={fetchBlogs}
              className="mt-4 px-6 py-2 bg-[#92AD36] text-white rounded-xl text-sm hover:bg-[#7a9230] transition"
            >
              Try Again
            </button>
          </div>
        )}

        {/* Empty */}
        {!loading && !error && filtered.length === 0 && (
          <div className="flex flex-col items-center py-20 text-center">
            <span className="text-5xl mb-4">📝</span>
            <p className="text-gray-600 font-medium text-lg">No blogs found</p>
            <p className="text-gray-400 text-sm mt-1">
              {search
                ? "Try a different search term"
                : "No blogs published yet"}
            </p>
            {search && (
              <button
                onClick={() => setSearch("")}
                className="mt-3 px-5 py-1.5 bg-[#92AD36] text-white rounded-lg text-xs"
              >
                Clear Search
              </button>
            )}
          </div>
        )}

        {/* ── Featured Blog (first blog, full width) ── */}
        {!loading && !error && featuredBlog && !search && currentPage === 1 && (
          <div
            // onClick={() => navigate(`/blog/${featuredBlog._id}`)}
            className="relative w-full rounded-2xl overflow-hidden shadow-md mb-10 cursor-pointer group"
          >
            <img
              src={featuredBlog.images?.[0]}
              alt={featuredBlog.title}
              className="w-full h-64 sm:h-80 md:h-96 object-cover group-hover:scale-105 transition-transform duration-500"
              onError={(e) => {
                e.target.src = "https://placehold.co/1200x400?text=Blog";
              }}
            />
            {/* Overlay */}
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />

            {/* Corner Badge */}
            <div className="absolute top-0 right-0 w-0 h-0 border-t-[70px] border-t-[#92AD36] border-l-[70px] border-l-transparent">
              <TfiPencil
                size={18}
                className="text-white absolute -top-14 right-2"
              />
            </div>

            {/* Featured Tag */}
            <span className="absolute top-5 left-5 bg-[#92AD36] text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wide">
              Featured
            </span>

            {/* Content */}
            <div className="absolute bottom-0 left-0 right-0 p-6">
              <div className="flex items-center gap-2 text-white/70 text-xs mb-3">
                <MdOutlineDateRange size={14} />
                {formatDate(
                  featuredBlog.publishedDate || featuredBlog.createdAt,
                )}
              </div>
              <h3 className="text-white text-xl sm:text-2xl md:text-3xl font-bold font-serif leading-snug line-clamp-2">
                {featuredBlog.title}
              </h3>
              <p className="text-white/70 text-sm mt-2 line-clamp-2 max-w-2xl">
                {stripHtml(featuredBlog.content)}
              </p>
              {/* <button className="mt-4 px-5 py-2 bg-[#92AD36] text-white text-sm rounded-xl hover:bg-white hover:text-[#92AD36] border border-[#92AD36] transition duration-300">
                Read More →
              </button> */}
            </div>
          </div>
        )}

        {/* ── Blog Grid ── */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {Array.from({ length: 8 }).map((_, i) => (
              <BlogCardSkeleton key={i} />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {(search ? paginated : restBlogs).map((item) => (
              <div
                key={item._id}
                // onClick={() => navigate(`/blog/${item._id}`)}
                className="bg-white rounded-2xl overflow-hidden shadow-sm border border-gray-100 hover:border-[#92AD36] hover:shadow-xl transition-all duration-300 group cursor-pointer"
              >
                {/* Image */}
                <div className="relative overflow-hidden h-52">
                  <img
                    src={item.images?.[0]}
                    alt={item.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    onError={(e) => {
                      e.target.src = "https://placehold.co/600x300?text=Blog";
                    }}
                  />
                  {/* Corner Badge */}
                  <div className="absolute top-0 right-0 w-0 h-0 border-t-[55px] border-t-[#92AD36] border-l-[55px] border-l-transparent">
                    <TfiPencil
                      size={15}
                      className="text-white absolute -top-11 right-1.5"
                    />
                  </div>
                </div>

                {/* Content */}
                <div className="p-5">
                  {/* Date */}
                  <div className="flex items-center gap-2 text-xs text-[#9DABAF] mb-3">
                    <MdOutlineDateRange size={13} />
                    {formatDate(item.publishedDate || item.createdAt)}
                  </div>

                  {/* Title */}
                  <h3 className="text-base font-bold text-black line-clamp-2 leading-snug group-hover:text-[#3A7138] transition-colors duration-200">
                    {item.title}
                  </h3>

                  {/* Excerpt */}
                  <p className="text-xs text-[#9DABAF] mt-2 line-clamp-3 leading-relaxed">
                    {stripHtml(item.content)}
                  </p>

                  {/* Divider */}
                  <div className="w-full h-px bg-gray-100 my-4" />

                  {/* Read More */}
                  {/* <button className="text-[#3A7138] text-xs font-semibold hover:text-[#92AD36] transition flex items-center gap-1">
                    Read More <span className="text-base">→</span>
                  </button> */}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* ── Pagination ── */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-2 mt-12">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              ← Prev
            </button>
            {Array.from({ length: totalPages }).map((_, i) => (
              <button
                key={i}
                onClick={() => {
                  setCurrentPage(i + 1);
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }}
                className={`w-9 h-9 rounded-lg text-sm font-medium transition ${
                  currentPage === i + 1
                    ? "bg-[#92AD36] text-white"
                    : "border border-gray-300 text-gray-600 hover:bg-gray-100"
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button
              onClick={() => {
                setCurrentPage((p) => Math.min(p + 1, totalPages));
                window.scrollTo({ top: 0, behavior: "smooth" });
              }}
              disabled={currentPage === totalPages}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-600 text-sm hover:bg-gray-100 disabled:opacity-40 disabled:cursor-not-allowed transition"
            >
              Next →
            </button>
          </div>
        )}

        {/* Results count */}
        {!loading && filtered.length > 0 && (
          <p className="text-center text-xs text-gray-400 mt-4">
            Showing{" "}
            <span className="font-semibold text-gray-600">
              {(currentPage - 1) * ITEMS_PER_PAGE + 1}–
              {Math.min(currentPage * ITEMS_PER_PAGE, filtered.length)}
            </span>{" "}
            of{" "}
            <span className="font-semibold text-gray-600">
              {filtered.length}
            </span>{" "}
            blogs
          </p>
        )}
      </div>
    </>
  );
};

export default BlogPage;
